import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  Plus,
  FileText,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  Settings
} from 'lucide-react';
import Navbar from './shared/Navbar.jsx';
import QuickEditModal from './QuickEditModal.jsx';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { makeRequest } = useApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [reports, setReports] = useState([]);
  const [shows, setShows] = useState([]);
  const [totalGuests, setTotalGuests] = useState(0);
  const [creditBalance, setCreditBalance] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      // Wait for Clerk to fully load AND confirm user is signed in
      if (!isLoaded || !user) {
        console.log('⏳ Dashboard waiting for Clerk... isLoaded:', isLoaded, 'user:', !!user);
        return;
      }

      console.log('🎯 Dashboard: Starting data fetch for user:', user.id);

      try {
        setLoading(true);
        setError(null);

        console.log('📡 Fetching dashboard data in parallel...');

        // Fetch all data in parallel for faster loading
        // Non-critical endpoints (reports, shows, stats, credits) have fallbacks
        // User data is critical and will throw to outer catch if it fails
        const [userResponse, reportsResponse, showsResponse, statsResponse, creditsResponse] = await Promise.all([
          makeRequest('users/me').then(res => {
            console.log('✅ User data fetched:', res);
            return res;
          }),
          makeRequest('reports').catch(err => {
            console.warn('⚠️ Non-critical error fetching reports:', err);
            return [];
          }),
          makeRequest('shows').catch(err => {
            console.warn('⚠️ Non-critical error fetching shows:', err);
            return [];
          }),
          makeRequest('users/me/stats').catch(err => {
            console.warn('⚠️ Non-critical error fetching stats:', err);
            return { total_guests: 0 };
          }),
          makeRequest('credits/balance').catch(err => {
            console.warn('⚠️ Non-critical error fetching credit balance:', err);
            return { trial: 0, basic: 0, pro: 0, total: 0, is_byok: false };
          })
        ]);

        console.log('📊 All data fetched successfully');

        if (!isMounted) {
          console.log('⚠️ Component unmounted, aborting');
          return;
        }

        // Validate critical user data
        if (!userResponse || !userResponse.clerk_user_id) {
          throw new Error('Invalid user data received from server');
        }

        // Set all state at once
        setUserData(userResponse);
        setReports(reportsResponse);
        setShows(showsResponse);
        setTotalGuests(statsResponse.total_guests);
        setCreditBalance(creditsResponse);

        console.log('📦 State updated:', {
          subscription_tier: userResponse.subscription_tier,
          is_lifetime: userResponse.is_lifetime,
          stripe_subscription_id: userResponse.stripe_subscription_id,
          shows_count: showsResponse.length
        });

        // Check access requirement (production only)
        // Allow access if user has: active subscription OR any remaining credits (including trial)
        if (import.meta.env.PROD) {
          const hasSubscription = userResponse.subscription_tier && (
            userResponse.is_lifetime || userResponse.stripe_subscription_id
          );
          const hasCredits = creditsResponse && (
            (creditsResponse.trial > 0) || (creditsResponse.basic > 0) || (creditsResponse.pro > 0) || creditsResponse.is_byok
          );

          console.log('🔐 Access check (PROD):', { hasSubscription, hasCredits });

          if (!hasSubscription && !hasCredits) {
            // No subscription and no credits — redirect to pricing
            console.log('❌ No subscription or credits, redirecting to pricing...');
            navigate('/');
            setTimeout(() => {
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            return;
          }
        }

        // Redirect users who haven't completed onboarding to intake form
        // This prevents showing confusing empty dashboard state
        if (showsResponse.length === 0) {
          console.log('📝 No shows found, redirecting to intake form...');
          navigate('/intake-form');
          return;
        }

        console.log('✅ Dashboard loaded successfully!');

      } catch (err) {
        console.error('❌ Critical error loading dashboard:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });

        if (!isMounted) return;

        // User data fetch failed - this is critical
        setError(err.message || 'Failed to load dashboard. Please try refreshing the page.');
      } finally {
        if (isMounted) {
          console.log('✅ Setting loading to false');
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, [isLoaded, user, navigate, makeRequest]); // Re-run when Clerk loads and user becomes available

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1rem',
        }}>
          <Loader2 size={48} style={{
            color: sharedStyles.colors.primary,
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{ color: sharedStyles.colors.textSecondary }}>
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1.5rem',
          padding: '2rem',
        }}>
          <div style={{
            ...createGlassCard('default'),
            padding: '2rem',
            maxWidth: '500px',
            textAlign: 'center',
          }}>
            <AlertCircle size={48} style={{
              color: sharedStyles.colors.error,
              marginBottom: '1rem',
            }} />
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: sharedStyles.colors.textPrimary,
              marginBottom: '0.5rem',
            }}>
              Error Loading Dashboard
            </h2>
            <p style={{ color: sharedStyles.colors.textSecondary }}>
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has completed onboarding (has at least one show)
  const hasCompletedOnboarding = shows.length > 0;
  const hasReports = reports.length > 0;

  // Handle immediate report generation
  const handleGenerateReport = async (customCriteria = null) => {
    if (!hasCompletedOnboarding) {
      // New users go to intake form
      navigate('/intake-form');
      return;
    }

    // Returning users: generate report (with optional custom criteria)
    setGeneratingReport(true);

    try {
      const requestBody = {
        show_id: shows[0].id  // Use first show (or most recent)
      };

      // Add custom criteria if provided from modal
      if (customCriteria) {
        if (customCriteria.guestTopics) requestBody.guest_topics = customCriteria.guestTopics;
        if (customCriteria.guestExpertise) requestBody.guest_expertise = customCriteria.guestExpertise;
        if (customCriteria.guestType?.length > 0) requestBody.guest_type = customCriteria.guestType;
        if (customCriteria.guestLocation) requestBody.guest_location = customCriteria.guestLocation;
        if (customCriteria.interviewFormat) requestBody.interview_format = customCriteria.interviewFormat;
        if (customCriteria.recordingMethod) requestBody.recording_method = customCriteria.recordingMethod;
        if (customCriteria.interviewLength) requestBody.interview_length = customCriteria.interviewLength;
        if (customCriteria.timeline) requestBody.timeline = customCriteria.timeline;
        if (customCriteria.additionalRequirements) requestBody.additional_requirements = customCriteria.additionalRequirements;
      }

      const response = await makeRequest('reports', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      // Reset loading state before navigation
      setGeneratingReport(false);

      // Navigate to the newly generated report
      navigate(`/report/${response.report_id}`);
    } catch (error) {
      console.error('Error generating report:', error);

      if (error.message.includes('402')) {
        alert('No credits available. Purchase credits or subscribe to generate reports.');
      } else if (error.message.includes('500')) {
        alert('The AI service encountered a temporary error. This usually resolves itself - please try again in a few seconds. If the problem persists, check https://status.anthropic.com/ for any ongoing issues.');
      } else {
        alert(`Failed to generate report: ${error.message}`);
      }
      setGeneratingReport(false);
    }
  };

  const handleOpenEditModal = () => {
    // Pre-fill modal with Show defaults if available
    const initialData = shows[0] ? {
      guestTopics: '',
      guestExpertise: '',
      guestType: [],
      guestLocation: '',
      interviewFormat: '',
      recordingMethod: '',
      interviewLength: '',
      timeline: '',
      additionalRequirements: ''
    } : {};

    setEditFormData(initialData);
    setShowEditModal(true);
  };

  const handleSaveAndGenerate = (formData) => {
    handleGenerateReport(formData);
  };

  return (
    <div style={sharedStyles.pageContainer}>
      <Navbar />

      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        padding: '1rem 4rem',
        overflow: 'visible'
      }}>
        <div style={{
          display: 'flex',
          gap: '1rem',
          maxWidth: '1200px',
          width: '100%',
          alignItems: 'stretch',
          maxHeight: 'calc(100vh - 200px)',
          minHeight: 'fit-content'
        }}>
          {/* Left Sidebar - Recent Reports */}
          {hasCompletedOnboarding && (
            <div style={{
              width: '320px',
              ...createGlassCard(),
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%',
              overflow: 'hidden'
            }}>
              {/* Panel Header */}
              <div style={{
                padding: '1rem 1.25rem',
                borderBottom: `1px solid ${sharedStyles.colors.primary}20`,
                background: sharedStyles.colors.surfaceAlt
              }}>
                <h2 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  margin: 0
                }}>
                  Recent Reports
                </h2>
              </div>

              {/* Reports List */}
              <div style={{
                padding: '0.75rem 1rem',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'auto',
                gap: '0.75rem'
              }}>
                {hasReports ? (
                  reports.map((report) => {
                    const show = shows.find(s => s.id === report.show_id);
                    const reportDate = new Date(report.created_at);

                    return (
                      <div
                        key={report.id}
                        onClick={() => navigate(`/report/${report.id}`)}
                        style={{
                          padding: '1rem',
                          background: report.is_completed ? sharedStyles.colors.surfaceAlt : `${sharedStyles.colors.error}10`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: report.is_completed ? `1px solid ${sharedStyles.colors.primary}20` : `1px solid ${sharedStyles.colors.error}30`,
                          opacity: report.is_completed ? 1 : 0.8
                        }}
                        onMouseEnter={(e) => {
                          if (report.is_completed) {
                            e.currentTarget.style.background = `${sharedStyles.colors.primary}10`;
                            e.currentTarget.style.borderColor = `${sharedStyles.colors.primary}30`;
                          } else {
                            e.currentTarget.style.background = `${sharedStyles.colors.error}20`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (report.is_completed) {
                            e.currentTarget.style.background = sharedStyles.colors.surfaceAlt;
                            e.currentTarget.style.borderColor = `${sharedStyles.colors.primary}20`;
                          } else {
                            e.currentTarget.style.background = `${sharedStyles.colors.error}10`;
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <h3 style={{
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            color: sharedStyles.colors.textPrimary,
                            flex: 1
                          }}>
                            {show?.title || 'Untitled Show'}
                          </h3>
                          {!report.is_completed && (
                            <div
                              title="This report did not complete successfully. Click to view details and regenerate."
                              style={{
                                flexShrink: 0,
                                marginLeft: '0.5rem',
                                cursor: 'help'
                              }}
                            >
                              <AlertCircle
                                size={16}
                                style={{
                                  color: sharedStyles.colors.error
                                }}
                              />
                            </div>
                          )}
                        </div>
                        {!report.is_completed && (
                          <p style={{
                            fontSize: '0.7rem',
                            color: sharedStyles.colors.error,
                            fontWeight: '500',
                            marginBottom: '0.5rem'
                          }}>
                            Generation Failed
                          </p>
                        )}
                        <p style={{
                          fontSize: '0.75rem',
                          color: sharedStyles.colors.textSecondary,
                          marginBottom: '0.5rem'
                        }}>
                          Host: {show?.host || 'Unknown'}
                        </p>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          color: sharedStyles.colors.textTertiary,
                          fontSize: '0.7rem'
                        }}>
                          <Clock size={12} />
                          <span>
                            {reportDate.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem 1rem',
                    color: sharedStyles.colors.textSecondary
                  }}>
                    <FileText size={32} style={{
                      color: sharedStyles.colors.textTertiary,
                      marginBottom: '0.5rem',
                      opacity: 0.5
                    }} />
                    <p style={{ fontSize: '0.875rem' }}>
                      No reports yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
          }}>
            {/* Welcome Header */}
            <div style={{
              marginBottom: '2rem',
            }}>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: sharedStyles.colors.textPrimary,
                marginBottom: '0.5rem',
              }}>
                Welcome back, {user?.firstName || 'there'}! 👋
              </h1>
              <p style={{
                fontSize: '1.125rem',
                color: sharedStyles.colors.textSecondary,
              }}>
                {hasCompletedOnboarding
                  ? 'Ready to discover your next great guest?'
                  : 'Let\'s get started finding amazing guests for your show!'}
              </p>
            </div>

            {/* Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem',
            }}>
              {/* Credit Balance */}
              <div
                style={{
                  ...createGlassCard('hover'),
                  padding: '1.5rem',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/purchase-credits')}
                title="Click to purchase more credits"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <FileText size={20} style={{ color: sharedStyles.colors.primary }} />
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: sharedStyles.colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Credit Balance
                  </h3>
                </div>
                <p style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: sharedStyles.colors.textPrimary,
                }}>
                  {creditBalance?.is_byok
                    ? '∞'
                    : (creditBalance?.total || 0)}
                </p>
                {creditBalance && !creditBalance.is_byok && (
                  <div style={{
                    marginTop: '0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.375rem',
                  }}>
                    {creditBalance.trial > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: sharedStyles.colors.textSecondary }}>
                        <span>Trial:</span>
                        <span style={{ fontWeight: '600' }}>{creditBalance.trial}</span>
                      </div>
                    )}
                    {creditBalance.basic > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: sharedStyles.colors.textSecondary }}>
                        <span>Basic:</span>
                        <span style={{ fontWeight: '600' }}>{creditBalance.basic}</span>
                      </div>
                    )}
                    {creditBalance.pro > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: sharedStyles.colors.textSecondary }}>
                        <span>Pro:</span>
                        <span style={{ fontWeight: '600' }}>{creditBalance.pro}</span>
                      </div>
                    )}
                  </div>
                )}
                {creditBalance?.is_byok && (
                  <p style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: sharedStyles.colors.textSecondary,
                  }}>
                    BYOK Enabled - Unlimited
                  </p>
                )}
                {!creditBalance?.is_byok && creditBalance?.total === 0 && (
                  <button
                    style={{
                      ...createButton('secondary', {
                        width: '100%',
                        justifyContent: 'center',
                        padding: '0.5rem',
                        fontSize: '0.75rem',
                        marginTop: '0.75rem',
                      }),
                    }}
                  >
                    Buy Credits
                  </button>
                )}
              </div>

              {/* Total Guests Found */}
              <div style={{
                ...createGlassCard('hover'),
                padding: '1.5rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <CheckCircle size={20} style={{ color: sharedStyles.colors.success }} />
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: sharedStyles.colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Total Guests Found
                  </h3>
                </div>
                <p style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: sharedStyles.colors.textPrimary,
                }}>
                  {totalGuests}
                </p>
              </div>
            </div>

            {/* Quick Actions - Only show for users who have completed onboarding */}
            {hasCompletedOnboarding && (
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                marginBottom: '3rem',
              }}>
                {/* Primary CTA - Generate Report */}
                <button
                  onClick={() => handleGenerateReport()}
                  disabled={generatingReport}
                  title={generatingReport ? "You can close this page and come back later - your report will continue generating in the background" : ""}
                  style={{
                    ...createButton('primary'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontSize: '1rem',
                    padding: '1rem 1.5rem',
                    opacity: generatingReport ? 0.8 : 1,
                    cursor: generatingReport ? 'wait' : 'pointer',
                    position: 'relative'
                  }}
                >
                  {generatingReport ? (
                    <>
                      <Loader2
                        size={20}
                        style={{
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                      <span>Generating... (30-60s)</span>
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      Generate New Report
                    </>
                  )}
                </button>

                {/* Edit Button - Opens modal to customize criteria */}
                <button
                  onClick={handleOpenEditModal}
                  disabled={generatingReport}
                  style={{
                    ...createButton('secondary'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontSize: '1rem',
                    padding: '1rem 1.5rem',
                    opacity: generatingReport ? 0.6 : 1,
                    cursor: generatingReport ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Settings size={20} />
                  Customize & Generate
                </button>
              </div>
            )}

            {/* Empty state for users who haven't completed onboarding */}
            {!hasCompletedOnboarding && (
              <div style={{
                ...createGlassCard('default'),
                padding: '3rem',
                textAlign: 'center',
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 1.5rem',
                  background: `linear-gradient(135deg, ${sharedStyles.colors.primary}, ${sharedStyles.colors.primaryDark})`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Plus size={40} style={{ color: 'white' }} />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  marginBottom: '0.5rem',
                }}>
                  Welcome to Conviviality! 🎙️
                </h3>
                <p style={{
                  color: sharedStyles.colors.textSecondary,
                  marginBottom: '2rem',
                  maxWidth: '500px',
                  margin: '0 auto 2rem',
                  lineHeight: '1.6',
                }}>
                  Let's find the perfect guests for your podcast. Our AI will analyze your show
                  and discover amazing people who would be a great fit.
                </p>
                <button
                  onClick={() => navigate('/intake-form')}
                  style={{
                    ...createButton('primary'),
                    fontSize: '1.125rem',
                    padding: '1rem 2rem',
                  }}
                >
                  Create Your First Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Edit Modal */}
      <QuickEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveAndGenerate}
        initialData={editFormData}
      />
    </div>
  );
};

export default Dashboard;
